import React from 'react';
import { Outlet } from 'react-router-dom'
import { Box, Stack, Typography, keyframes } from '@mui/material';
import { BoltOutlined, GroupsOutlined, InsightsOutlined } from '@mui/icons-material';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
    0%, 100% { transform: translate3d(0, 0, 0); }
    50% { transform: translate3d(0, -18px, 0); }
`;

const HIGHLIGHTS = [
    { icon: <GroupsOutlined fontSize='small' />, title: 'Vos candidats au même endroit', text: 'Inscriptions, documents et retours candidats centralisés.' },
    { icon: <BoltOutlined fontSize='small' />, title: 'Des missions pourvues plus vite', text: 'Publiez vos offres et suivez les candidatures en temps réel.' },
    { icon: <InsightsOutlined fontSize='small' />, title: 'Une vision claire de votre activité', text: 'Tableaux de bord et statistiques par agence.' },
];

const AuthLayout = () => {
    return (
        <Box component='main' sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.paper' }}>
            {/* Brand panel */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    flex: '0 0 46%',
                    position: 'relative',
                    overflow: 'hidden',
                    p: { md: 6, lg: 8 },
                    color: '#fff',
                    background: 'linear-gradient(160deg, #00214D 0%, #0B3A7A 55%, #2E6BEB 100%)',
                }}
            >
                {/* Decorative blobs */}
                <Box sx={{
                    position: 'absolute', width: 520, height: 520, borderRadius: '50%', top: -180, right: -160,
                    background: 'radial-gradient(circle at 30% 30%, rgba(51,176,56,0.55), rgba(51,176,56,0) 70%)',
                    animation: `${float} 12s ease-in-out infinite`,
                }} />
                <Box sx={{
                    position: 'absolute', width: 420, height: 420, borderRadius: '50%', bottom: -160, left: -120,
                    background: 'radial-gradient(circle at 60% 40%, rgba(255,255,255,0.18), rgba(255,255,255,0) 70%)',
                    animation: `${float} 16s ease-in-out infinite reverse`,
                }} />
                <Box sx={{
                    position: 'absolute', inset: 0, opacity: 0.35,
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
                    backgroundSize: '48px 48px',
                    maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                }} />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                        component='img'
                        alt='Chronos'
                        src={process.env.PUBLIC_URL + '/hlogo.svg'}
                        sx={{ width: 170, filter: 'brightness(0) invert(1)' }}
                    />
                </Box>

                <Box sx={{ position: 'relative', zIndex: 1, animation: `${fadeUp} 0.6s ease-out both` }}>
                    <Typography component='p' sx={{ fontSize: { md: '2rem', lg: '2.5rem' }, fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2 }}>
                        Pilotez votre agence,<br />simplement.
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '1rem', maxWidth: 440, mb: 5 }}>
                        L'espace de gestion Chronos pour suivre vos candidats, vos offres et vos missions au quotidien.
                    </Typography>

                    <Stack spacing={2.5}>
                        {HIGHLIGHTS.map((item, index) => (
                            <Stack key={item.title} direction='row' spacing={2} alignItems='flex-start' sx={{ animation: `${fadeUp} 0.6s ease-out both`, animationDelay: `${0.15 + index * 0.1}s` }}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    width: 36, height: 36, borderRadius: '10px',
                                    bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
                                }}>
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#fff' }}>{item.title}</Typography>
                                    <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.65)' }}>{item.text}</Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                <Typography sx={{ position: 'relative', zIndex: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                    © {new Date().getFullYear()} Chronos — Tableau de bord agence
                </Typography>
            </Box>

            {/* Form panel */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 3, sm: 6 },
                py: { xs: 6, md: 8 },
                bgcolor: 'background.default',
            }}>
                <Box sx={{ width: '100%', maxWidth: 400, animation: `${fadeUp} 0.5s ease-out both` }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default AuthLayout;
